class KeyGen {
	wrappedKey;
	salt;
	iv;
	isEditMode;

	constructor(password_min_length) {
		this.createKeyButton = document.querySelector("#crypt-create-key");
		this.passwordField = document.querySelector("#crypt-password");
		this.passwordConfirmationField = document.querySelector(
			"#crypt-password-confirmation",
		);
		this.exportKeyOutput = document.querySelector("#crypt-public-key");
		this.wrappedKeyOutput = document.querySelector("#crypt-wrapped-key");
		this.ivOutput = document.querySelector("#crypt-iv");
		this.saltOutput = document.querySelector("#crypt-salt");
		this.cryptoForm = document.querySelector("#crypt-form");
		this.password_min_length = password_min_length;

		// Determine if we're in edit mode (no password fields) or create mode (with password fields)
		this.isEditMode = !this.passwordField || !this.passwordConfirmationField;

		// Check for required fields (password fields are optional for edit forms)
		const requiredFields = [
			this.exportKeyOutput,
			this.wrappedKeyOutput,
			this.ivOutput,
			this.saltOutput,
			this.cryptoForm,
		];

		const missingRequiredFields = requiredFields.some((ele) => ele === null);

		if (missingRequiredFields) {
			alert("Could not find all required fields for KeyGen script!");
		}
	}

	canGenerateKeys() {
		return !this.isEditMode && this.passwordField && this.passwordConfirmationField;
	}

	fillForm() {
		this.exportKeyOutput.value = this.publicKey;
		this.wrappedKeyOutput.value = this.wrappedKey;
		this.ivOutput.value = window.btoa(this.ab2str(this.iv));
		this.saltOutput.value = window.btoa(this.ab2str(this.salt));
	}

	async checkPassword() {
		// Only check password if this instance can generate keys
		if (!this.canGenerateKeys()) {
			return;
		}

		if (this.passwordField.value !== this.passwordConfirmationField.value) {
			alert("Password does not match password confirmation.");
			return;
		}
		// Get the minimum password length from the environment variable or set it to 11 if it's less than 11 or unset
		const minimumPasswordLength = Math.max(this.password_min_length || 11, 11);
		if (this.passwordField.value.length < minimumPasswordLength) {
			alert(
				`Please enter a password with at least ${minimumPasswordLength} characters to encrypt the data.`,
			);
			return;
		}
		//check if password is secure
		await this.hibpCheck(this.passwordField.value);
	}

	async sha1(source) {
		const sourceBytes = new TextEncoder().encode(source);
		const digest = await crypto.subtle.digest("SHA-1", sourceBytes);
		const resultBytes = [...new Uint8Array(digest)];
		return resultBytes.map((x) => x.toString(16).padStart(2, "0")).join("");
	}

	async hibpCheck(pwd) {
		// We hash the pwd first
		const hash = await this.sha1(pwd);
		// We send the first 5 chars of the hash to hibp's API
		const req = new XMLHttpRequest();
		req.addEventListener("load", function () {
			// When we get back a response from the server
			// We create an array of lines and loop through them
			const resp = this.responseText.split("\n");
			const hashSub = hash.slice(5).toUpperCase();
			let result = false;
			for (const index in resp) {
				// Check if the line matches the rest of the hash
				if (resp[index].substring(0, 35) === hashSub) {
					result = true;
					break; // If found no need to continue the loop
				}
			}
			// Trigger an event with the result
			const event = new CustomEvent("hibpCheck", { detail: result });
			document.dispatchEvent(event);
		});
		req.open(
			"GET",
			`https://api.pwnedpasswords.com/range/${hash.substring(0, 5)}`,
		);
		req.send();
	}

	async generateKeyPair() {
		// Only generate key pair if this instance can generate keys
		if (!this.canGenerateKeys()) {
			throw new Error("Password fields are required to generate a new key pair.");
		}

		const password = this.passwordField.value;

		if (this.passwordConfirmationField.value !== password) {
			throw new Error("Password and confirmation do not match.");
		}

		const minimumPasswordLength = Math.max(this.password_min_length || 11, 11);
		if (password.length < minimumPasswordLength) {
			throw new Error(
				`Password must be at least ${minimumPasswordLength} characters.`,
			);
		}

		const keyPair = await crypto.subtle.generateKey(
			{
				name: "RSA-OAEP",
				modulusLength: 2048,
				publicExponent: new Uint8Array([1, 0, 1]),
				hash: "SHA-256",
			},
			true,
			["encrypt", "decrypt"],
		);

		await this.exportPublicKey(keyPair.publicKey);
		await this.wrapCryptoKey(keyPair.privateKey);
	}

	async getKeyMaterial() {
		if (!this.canGenerateKeys()) {
			throw new Error("Password field is required to generate key material.");
		}

		const password = this.passwordField.value;
		return crypto.subtle.importKey(
			"raw",
			new TextEncoder().encode(password),
			{ name: "PBKDF2" },
			false,
			["deriveBits", "deriveKey"],
		);
	}

	async getKey(keyMaterial, salt) {
		return crypto.subtle.deriveKey(
			{
				name: "PBKDF2",
				salt,
				iterations: 100000,
				hash: "SHA-256",
			},
			keyMaterial,
			{ name: "AES-CBC", length: 256 },
			true,
			["wrapKey", "unwrapKey"],
		);
	}

	async wrapCryptoKey(privateKey) {
		const keyMaterial = await this.getKeyMaterial();
		this.salt = crypto.getRandomValues(new Uint8Array(16));
		const wrappingKey = await this.getKey(keyMaterial, this.salt);
		this.iv = crypto.getRandomValues(new Uint8Array(16));

		const wrappedKey = await crypto.subtle.wrapKey(
			"pkcs8",
			privateKey,
			wrappingKey,
			{ name: "AES-CBC", iv: this.iv },
		);

		this.wrappedKey = window.btoa(this.ab2str(wrappedKey));

		document.dispatchEvent(
			new CustomEvent("keypair:ready", {
				detail: { status: "ok" },
			}),
		);
	}

	async exportPublicKey(publicKey) {
		const exported = await crypto.subtle.exportKey("spki", publicKey);
		const exportedAsString = this.ab2str(exported);
		this.publicKey = window.btoa(exportedAsString);
	}

	exportWrappedKey(key) {
		const exportedAsString = this.ab2str(key);
		this.wrappedKey = window.btoa(exportedAsString);
	}

	ab2str(buf) {
		return String.fromCharCode.apply(null, new Uint8Array(buf));
	}
}

window.KeyGen = KeyGen;

export default KeyGen;
